export interface FoldRange {
  endLine: number
  startLine: number
}

const lineIndent = (line: string): number =>
  line.length - line.trimStart().length

const opensFoldableContainer = (line: string): boolean => {
  const trimmed = line.trimEnd()
  return trimmed.endsWith("{") || trimmed.endsWith("[")
}

/**
 * Finds multi-line object/array fold ranges in pretty-printed JSON from
 * `JSON.stringify(..., null, 2)`. Line numbers are 1-indexed.
 * Same-line `{}` / `[]` are omitted.
 *
 * A fold starts on a line ending with `{` or `[` and ends on the next
 * non-empty line at the same or smaller indent (stringify-shaped output only).
 * Single-pass stack: O(n) in the number of lines.
 */
export const computeFoldRanges = (prettyJson: string): FoldRange[] => {
  const lines = prettyJson.split("\n")
  const ranges: FoldRange[] = []
  const open: { startIndent: number; startLine: number }[] = []

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? ""
    if (line.trim().length === 0) {
      continue
    }

    const indent = lineIndent(line)

    while (open.length > 0) {
      const top = open.at(-1)
      if (top === undefined || indent > top.startIndent) {
        break
      }
      open.pop()
      if (lineIndex + 1 > top.startLine) {
        ranges.push({ endLine: lineIndex + 1, startLine: top.startLine })
      }
    }

    if (opensFoldableContainer(line)) {
      open.push({ startIndent: indent, startLine: lineIndex + 1 })
    }
  }

  ranges.sort((a, b) => a.startLine - b.startLine)
  return ranges
}
